import React, { useState, useEffect, useMemo  } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Loader from '../../Components/Loader/Loader';
import './Skeleton.scss';

const SkeletonComponent = ({ variant, showAlmostThere, rows=3 }) => {
    const currentMessages = useMemo(() => {
        const variantMessages = {
            sidebar: [
                "Loading your portfolio snapshot...",
                "Generating your portfolio dashboard...",
                "Finalizing the view..."
            ],
            customerportfolio: [
                "Loading your portfolio snapshot...",
                "Generating your portfolio dashboard...",
                "Finalizing the view..."
            ],
            portfoliolineitems: [
                "Preparing your holdings data...",
                "Calculating market values and weights...",
                "Finalizing your portfolio view..."
            ],
            summary: [
                "Analyzing market risks and volatility...",
                "Calculating your hedging potential...",
                "Building your market shield backtest...",
                "Finalizing protection strategy data..."
            ],
            riskcontribution: [
                "Calculating individual stock risk exposures...",
                "Estimating market, idiosyncratic, and diversified risk...",
                "Visualizing portfolio volatility impact...",
                "Finalizing risk contribution analysis..."
            ],
            factoranalysis: [
                "Fetching factor exposures...",
                "Analyzing benchmark data...",
                "Calculating volatility...",
                "Crunching performance metrics..."
            ],
            marketshieldbacktest: [
                "Loading historical market scenarios...",
                "Calculating portfolio downside risk...",
                "Generating backtest performance charts...",
                "Finalizing asset allocation insights..."
            ],
            summarybacktest: [
                "Analyzing market risks and volatility...",
                "Calculating your hedging potential...",
                "Building your market shield backtest...",
                "Finalizing protection strategy data..."
            ],
            forwardtest: [
                "Loading simulation data...",
                "Initializing forward simulation...",
                "Estimating future market conditions...",
                "Simulating portfolio performance...",
                "Calculating risk-adjusted returns...",
                "Evaluating protection strategies...",
                "Finalizing forward simulation results..."
            ],
            shieldrecommendation: [
                "Loading your market shield strategy...",
                "Calculating protection level...",
                "Estimating downside volatility...",
                "Building risk score analysis...",
                "Analyzing strike price scenarios...",
                "Evaluating hedging opportunities...",
                "Computing protection costs...",
                "Finalizing market shield recommendations..."
            ],
            recommendationdashboard: [
                "Loading portfolio data...",
                "Fetching option chain details...",
                "Calculating strike prices...",
                "Estimating premiums..."
            ],
            profile: [
                "Loading your profile...",
                "Fetching your subscription details...",
                "Retrieving payment information...",
                "Almost there, just a moment..."
            ],
            multiuser: [
                "Loading sub user data...",
                "Fetching account details...",
                "Preparing your dashboard...",
                "Getting everything ready..."
            ],

        };
        return variantMessages[variant] || [];
    }, [variant]);

    const columnWidths = [
        4,  
        4,  
        8,  
        20, 
        10, 
        14, 
        10, 
        10, 
        10, 
        10, 
        10, 
    ];

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

    useEffect(() => {
        if (!showAlmostThere || currentMessages.length === 0) return;

        const interval = setInterval(() => {
            setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % currentMessages.length);
        }, 1750);
        return () => clearInterval(interval);
    }, [showAlmostThere,currentMessages]);

    const portfolioDetailsTableSkeleton = () => (
        <div className="table-skeleton-wrapper">
            <div className="table-header-skeleton">
                {columnWidths.map((width, idx) => (
                    <Skeleton animation="wave"
                        key={idx}
                        variant="text"
                        height={28}
                        width={`${width}%`}
                        
                    />
                ))}
            </div>
            <div className={`table-rows-scrollable ${rows > 5 ? 'scroll-enabled' : ''}`}>    
                {[...Array(rows)].map((_, rowIdx) => (
                    <div
                    key={rowIdx}
                    className="table-row-skeleton animated-row"
                    style={{ animationDelay: `${rowIdx * 80}ms` }}
                    >
                    {columnWidths.map((width, colIdx) => (
                        <Skeleton animation="wave"
                        key={colIdx}
                        variant="text"
                        height={28}
                        width={`${width}%`}
                        
                        />
                    ))}
                    </div>
                ))}
            </div>

            <div className="table-total-skeleton">
                {columnWidths.map((width, idx) => {
                    if (idx === 3) {
                        return (
                        <Skeleton animation="wave"
                            key={idx}
                            variant="text"
                            height={28}
                            width="30%"
                            
                        />
                        );
                    } else if (idx >= columnWidths.length - 3) {
                        return (
                        <Skeleton animation="wave"
                            key={idx}
                            variant="text"
                            height={28}
                            width={`${width}%`}
                            
                        />
                        );
                    } else {
                        return <div key={idx} style={{ width: `${width}%` }} />;
                    }
                })}
            </div>
        </div>
    );

  switch (variant) {
    case 'sidebar':
      return (
        <div className="sidebar-loading">
          {Array.from({ length: 2 }).map((_, index) => (
                <div className="clientcard skeleton-card" key={index}>
                <div className="clientdata">
                    <Skeleton animation="wave" width="50%" height={18}  />
                    <div className="skeleton-icons">
                    <Skeleton animation="wave" variant="circular" width={20} height={20}  />
                    <Skeleton animation="wave" variant="circular" width={20} height={20}  />
                    <Skeleton animation="wave" variant="circular" width={20} height={20}  />
                    </div>
                </div>
                <div className="portfoliodata">
                    <Skeleton animation="wave" width="40%" height={14}  />
                </div>
                <div className="portfoliodata">
                    <Skeleton animation="wave" width="40%" height={14}  />
                </div>
                </div>
            ))}
            {showAlmostThere && (
                <div className="sidebar-loader-wrapper">
                    <p className="loading-message">{currentMessages[currentMessageIndex]}</p>
                </div>
            )}
            <div className="sidebar-loader-wrapper">
                <Loader size="small" />
            </div>
        </div>
      );

    case 'navbar':
      return (
        <div className="navbar-right navbar-skeleton">
            <div className="user-info">
                <div className="user-avatar">
                    <Skeleton animation="wave" variant="circular" width={40} height={40} />
                </div>
                <div className="user-detail">
                    <Skeleton animation="wave" variant="text" width={100} height={20} />
                    <Skeleton animation="wave" variant="text" width={60} height={16} />
                </div>
            </div>
        </div>
      );

    case 'buttons':
      return (
        <div className="sidebar-buttons-skeleton">
          <div className="skeleton-btn" />
          <div className="skeleton-btn" />
        </div>
      );
    
    case 'customerportfolio':
        return (
            <div className="customer-portfolio-skeleton">
                {Array.from({ length: 2 }).map((_, customerIndex) => (
                    <div className="customer-skeleton" key={customerIndex}>
                        <div className="portfoliohead-skeleton">
                            <Skeleton animation="wave" variant="text" width="40%" height={46} />
                            <div className="barbutton-skeleton">
                                <Skeleton animation="wave" variant="rectangular" width={36} height={36} />
                                <Skeleton animation="wave" variant="rectangular" width={160} height={36} />
                            </div>
                        </div>
                        <div className="portfolios-skeleton">
                            {Array.from({ length: 2 }).map((_, portfolioIndex) => (
                                <div className="portfoliocard-skeleton" key={portfolioIndex}>
                                    <div className="portlist-skeleton">
                                        <div className="listhead-skeleton">
                                            <Skeleton animation="wave" variant="text" width="160px" height={24} />
                                            <Skeleton animation="wave" variant="text" width="120px" height={24} />
                                            <Skeleton animation="wave" variant="text" width="160px" height={24} />
                                            <div className="barbutton-skeleton">
                                                <Skeleton animation="wave" variant="rectangular" width={36} height={36} />
                                                <Skeleton animation="wave" variant="rectangular" width={36} height={36} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                {showAlmostThere && (
                    <div className="skeleton-loader-wrapper">
                        <p className="skeleton-loading-message">{currentMessages[currentMessageIndex]}</p>
                    </div>
                )}
                <div className="skeleton-loader-wrapper">
                    <Loader size="big" />
                </div>
            </div>
        );
    case 'portfoliolineitems':
        return (
            <div className="portfolio-lineitems-skeleton">
                <div className="toggle-skeleton">
                    <Skeleton animation="wave" variant="text" width="10%" height={24}/>
                    <Skeleton animation="wave" variant="text" width="3%" height={24}/>
                    <Skeleton animation="wave" variant="text" width="10%" height={24}/>
                    <Skeleton animation="wave" variant="rectangular" width="10%" height={26} />
                    <Skeleton animation="wave" variant="rectangular" width="10%" height={26} />
                    <Skeleton animation="wave" variant="text" width="5%" height={24}/>
                </div>
                <div className="lineitem-header">
                    <Skeleton animation="wave" variant="rectangular" width="2%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="5%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="15%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="5%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="8%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="8%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="8%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="8%" height={24} />
                    <Skeleton animation="wave" variant="rectangular" width="5%" height={24} />
                </div>
                
                {[...Array(1)].map((_, index) => (
                    <div className="lineitem-row" key={index}>
                        <Skeleton animation="wave" variant="text" width="2%" height={24} />
                        <Skeleton animation="wave" variant="text" height={24} width="5%" />
                        <Skeleton animation="wave" variant="text" height={24} width="15%" />
                        <Skeleton animation="wave" variant="text" height={24} width="5%" />
                        <Skeleton animation="wave" variant="text" height={24} width="8%" />
                        <Skeleton animation="wave" variant="text" height={24} width="8%" />
                        <Skeleton animation="wave" variant="text" height={24} width="8%" />
                        <Skeleton animation="wave" variant="text" height={24} width="8%" />
                        <Skeleton animation="wave" variant="circular" width={24} height={24} />
                    </div>
                ))}

                <div className="input-fields-skeleton">
                    <Skeleton animation="wave" variant="rectangular" width="35%" height={36} />
                    <Skeleton animation="wave" variant="rectangular" width="20%" height={36} />
                    <Skeleton animation="wave" variant="rectangular" width="36px" height={36} />
                </div>
                <div className="skeleton-loader-wrapper">
                    <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                        {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                    </p>
                </div>
                <div className="skeleton-loader-wrapper">
                    <Loader size="small" />
                </div>
            </div>
        );

        case 'summary':
            return (
                <div className="summary-analytics-skeleton">
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    <div className="chart-skeleton">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={360} />
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                            {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="backtest-summary-skeleton">
                        <div className="summary-column">
                        {[...Array(8)].map((_, index) => (
                            <div className="summary-row-skeleton" key={`left-${index}`}>
                            <Skeleton animation="wave" variant="text" width="30%" height={20} className='row-left'/>
                            <Skeleton animation="wave" variant="rectangular" width="30%" height={20} />
                            </div>
                        ))}
                        </div>
                        <div className="summary-column">
                        {[...Array(8)].map((_, index) => (
                            <div className="summary-row-skeleton" key={`right-${index}`}>
                            <Skeleton animation="wave" variant="text" width="30%" height={20} className='row-left'/>
                            <Skeleton animation="wave" variant="rectangular" width="30%" height={20} />
                            </div>
                        ))}
                        </div>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                </div>
            );
        
        case 'summarybacktest':
            return (
                <div className="summary-analytics-skeleton">
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    <div className="chart-skeleton">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={360} />
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                            {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                </div>
            );
                
        case 'riskcontribution':
            return (
                <div className="risk-contribution-skeleton">
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>

                    <div className="bar-chart-skeleton-wrapper">
                        <div className="y-axis">
                            {Array.from({ length: Math.ceil(rows / 2) }).map((_, idx) => (
                                <div key={idx} className="y-tick">
                                    <Skeleton animation="wave" variant="text" height={15} width={20} />
                                </div>
                            ))}
                        </div>

                        <div className="bar-chart-skeleton">
                            {Array.from({ length: rows }).map((_, index) => (
                                <div className="bar-skeleton" key={index}>
                                    <div className={`animated-bar delay-${index % 5}`}>
                                        <Skeleton animation="wave"
                                            variant="rectangular"
                                            className="bar"
                                            height={100 + (index % 5) * 20}
                                        />
                                    </div>
                                    <Skeleton animation="wave"
                                        variant="text"
                                        className="bar-label"
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="y-axis">
                            {Array.from({ length: Math.ceil(rows / 2) }).map((_, idx) => (
                                <div key={idx} className="y-tick">
                                    <Skeleton animation="wave" variant="text" height={15} width={20} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bar-chart-legend-skeleton">
                        <div className="legend-item-skeleton">
                            <Skeleton animation="wave" variant="circular" width={14} height={14} />
                            <Skeleton animation="wave" variant="text" width={180} height={14} />
                        </div>
                        <div className="legend-item-skeleton">
                            <Skeleton animation="wave" variant="circular" width={14} height={14} />
                            <Skeleton animation="wave" variant="text" width={200} height={14} />
                        </div>
                        <div className="legend-item-skeleton">
                            <Skeleton animation="wave" variant="circular" width={14} height={14} />
                            <Skeleton animation="wave" variant="text" width={170} height={14} />
                        </div>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                            {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    {portfolioDetailsTableSkeleton()}
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                </div>
            );
        case 'factoranalysis':
            return(
                <div className="factor-analysis-skeleton">
                    <div className="skeleton-top-cards">
                        <div className="skeleton-card factor-analysis-card">
                            <div className="header">
                                <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                            </div>
                            <div className="table-skeleton-wrapper">
                                {[...Array(3)].map((_, rowIdx) => (
                                <div key={rowIdx} className="table-row-skeleton">
                                    <Skeleton animation="wave" variant="text" width="40%" height={32} />
                                    <Skeleton animation="wave"
                                    className={rowIdx === 0 ? 'hidden' : 'visible'}
                                    variant="text"
                                    width="20%"
                                    height={32}
                                    />
                                    <Skeleton animation="wave" variant="text" width="20%" height={32} />
                                    <Skeleton animation="wave" variant="text" width="20%" height={32} />
                                </div>
                                ))}
                            </div>
                        </div>
                        <div className="skeleton-card benchmark-chart-card">
                            <div className="header">
                                <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                            </div>

                            <div className="venn-section">
                                {[...Array(2)].map((_, idx) => (
                                <div key={idx} className="venn-group">
                                    <Skeleton animation="wave" className="venn-title-skeleton" variant="text" width="60%" height={22} />

                                    <div className="venn-diagram-skeleton">
                                        <Skeleton animation="wave" variant="circular" width={120} height={120} className="circle-left" />
                                        <Skeleton animation="wave" variant="circular" width={120} height={120} className="circle-right" />
                                    </div>

                                    <div className="stats-table">
                                    {[...Array(3)].map((_, i) => (
                                        <React.Fragment key={i}>
                                        <Skeleton animation="wave" variant="text" width={80} height={16} />
                                        <Skeleton animation="wave" variant="rectangular" width={60} height={24} />
                                        </React.Fragment>
                                    ))}
                                    </div>
                                </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                        {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="header">
                            <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    {portfolioDetailsTableSkeleton()}
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                </div>
            )
         case 'marketshieldbacktest':
            return (
                <div className="market-shield-scroll-container">
                <div className="market-shield-backtest-skeleton">
                    <div className="market-shield-top-row">
                        <Skeleton animation="wave" variant="text" className="title-skeleton" />
                        <div className="filters-skeleton">
                            {[...Array(3)].map((_, i) => (
                            <div key={i} className="filter-card">
                                <Skeleton animation="wave" variant="text" className="filter-label" />
                                <Skeleton animation="wave" variant="rectangular" className="filter-select" />
                            </div>
                            ))}
                        </div>
                    </div>
                    <div className="event-cards-skeleton">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="event-card">
                                <Skeleton animation="wave" variant="rectangular" width="200px" height={80} className="event-card-rect" />
                            </div>
                        ))}
                    </div>

                    <Skeleton animation="wave" variant="rectangular" width="100%" height={320} className="main-chart-skeleton" />
                    <div className="date-range-skeleton">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton animation="wave" key={i} variant="rectangular" width={36} height={36} />
                        ))}
                        <Skeleton animation="wave" variant="rectangular" width={150} height={36} />
                        <Skeleton animation="wave" variant="rectangular" width={150} height={36} />
                        <Skeleton animation="wave" variant="rectangular" width={100} height={36} />
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                        {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>

                    <div className="stats-grid-skeleton">
                        <div className="stats-labels">
                            {[...Array(3)].map((_, i) => (
                            <Skeleton animation="wave"
                                key={i}
                                variant="text"
                                width={180}
                                height={24}
                                className="stats-label"
                            />
                            ))}
                        </div>
                        {[...Array(4)].map((_, colIdx) => (
                            <div key={colIdx} className="stats-column">
                            <Skeleton animation="wave"
                                variant="rectangular"
                                width={180}
                                height={28}
                                className="stats-header"
                            />
                            {[...Array(3)].map((_, rowIdx) => (
                                <Skeleton animation="wave"
                                key={rowIdx}
                                variant="text"
                                width={100}
                                height={24}
                                className="stats-value"
                                />
                            ))}
                            </div>
                        ))}
                        </div>
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    {portfolioDetailsTableSkeleton()}
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                </div>
                </div>
            );
        case 'forwardtest':
            return(
                <div className="market-shield-scroll-container">
                <div className="forward-test-skeleton">
                    <div className="forward-test-top-row">
                        <div className="select-skeletons">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="select-card">
                                <Skeleton animation="wave" variant="text" width="200px" height={36} />
                                <Skeleton animation="wave" variant="rectangular" width="200px" height={36} />
                            </div>
                        ))}
                        </div>
                        <div className="simulation-button-skeleton">
                            <Skeleton animation="wave" variant="rectangular" width={150} height={50} />
                        </div>
                    </div>
                    <div className="forward-test-charts-skeleton">
                        <div className="charts-row">
                            <div className="chart-column">
                            <Skeleton animation="wave" variant="text" width="60%" height={28} className="chart-title" />
                            <Skeleton animation="wave" variant="rectangular" width="100%" height={280} className="chart-skeleton" />
                            </div>
                            <div className="chart-column">
                            <Skeleton animation="wave" variant="text" width="60%" height={28} className="chart-title" />
                            <Skeleton animation="wave" variant="rectangular" width="100%" height={280} className="chart-skeleton" />
                            </div>
                        </div>
                        <div className="charts-row">
                            <div className="chart-column">
                            <Skeleton animation="wave" variant="text" width="60%" height={28} className="chart-title" />
                            <Skeleton animation="wave" variant="rectangular" width="100%" height={280} className="chart-skeleton" />
                            </div>
                            <div className="chart-column">
                            <Skeleton animation="wave" variant="text" width="60%" height={28} className="chart-title" />
                            <Skeleton animation="wave" variant="rectangular" width="100%" height={280} className="chart-skeleton" />
                            </div>
                        </div>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                        {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="100%" height={36} />
                    </div>
                    {portfolioDetailsTableSkeleton()}
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                    
                </div>
                </div>
            )
        case 'shieldrecommendation':
            return (
                <div className="shield-recommendation-skeleton">
                    <div className="header-skeleton-container">
                        <div className="breadcrumb-skeleton">
                            <Skeleton animation="wave" variant="rectangular" width={100} height={28} />
                            <Skeleton animation="wave" variant="text" width={360} height={36}  />
                        </div>

                        <div className="top-actions-skeleton">
                            <div className="tabs-skeleton">
                            {[280, 200, 200].map((width, i) => (
                                <Skeleton animation="wave" key={i} variant="rectangular" width={width} height={36}  />
                            ))}
                            </div>
                            <div className="buttons-skeleton">
                            <Skeleton animation="wave" variant="rectangular" width={180} height={36}  />
                            <Skeleton animation="wave" variant="circular" width={32} height={32}  />
                            </div>
                        </div>
                    </div>
                    <div className="shield-top-row">
                        <div className="select-skeletons">
                            {[...Array(2)].map((_, index) => (
                            <div key={index} className="select-card">
                                <Skeleton animation="wave" variant="text" width="200px" height={36} />
                                <Skeleton animation="wave" variant="rectangular" width="200px" height={36} />
                            </div>
                            ))}
                        </div>
                        <div className="filters-skeleton">
                            <Skeleton animation="wave" variant="rectangular" width="100px" height={76}  className="filter-label" />
                            <Skeleton animation="wave" variant="rectangular" width="150px" height={76}  className="filter-select" />
                        </div>
                        <div className="protection-slider-skeleton">
                            <div className="slider-labels-top">
                                {[...Array(6)].map((_, i) => (
                                    <div className="slider-column" key={`top-${i}`}>
                                        <Skeleton animation="wave" variant="text" width={40} height={18} />
                                        <Skeleton animation="wave" variant="text" width={30} height={18} />
                                    </div>
                                ))}
                            </div>
                            <div className="slider-bar">
                                <Skeleton animation="wave" variant="rectangular" width="100%" height={12} className="slider-track" />
                                <Skeleton animation="wave" variant="circular" width={16} height={16} className="slider-thumb" />
                                <div className="slider-tooltip">
                                    <Skeleton animation="wave" variant="text" width={40} height={50} />
                                </div>
                            </div>
                            <div className="slider-labels-bottom">
                                {[...Array(6)].map((_, i) => (
                                    <div className="slider-column" key={`bottom-${i}`}>
                                    <Skeleton animation="wave" variant="text" width={50} height={18} />
                                    <Skeleton animation="wave" variant="text" width={60} height={18} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="metrics-grid-skeleton">
                        {[...Array(2)].map((_, i) => (
                            <div key={`metric-left-${i}`} className="skeleton-cell">
                                <Skeleton animation="wave" variant="rectangular" className="skeleton-box small" />
                                <Skeleton animation="wave" variant="rectangular" className="skeleton-box large" />
                            </div>
                        ))}
                        <div className="skeleton-cell skeleton-cost dark-bg" style={{ gridRow: '1 / span 2' }}>
                            {[...Array(4)].map((_, idx) => (
                            <Skeleton animation="wave" key={idx} variant="rectangular" className="skeleton-box cost-line" />
                            ))}
                        </div>
                        {[...Array(2)].map((_, i) => (
                            <div key={`metric-right-${i}`} className="skeleton-cell">
                            <Skeleton animation="wave" variant="rectangular" className="skeleton-box small" />
                            <Skeleton animation="wave" variant="rectangular" className="skeleton-box large" />
                            </div>
                        ))}
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                            {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                        </p>
                    </div>
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                    <div className="market-shield-skeleton">
                        <div className="title">
                            <Skeleton animation="wave" variant="rectangular" width={360} height={36}  />
                        </div>
                        <div className="grid">
                            {[...Array(6)].map((_, idx) => (
                            <Skeleton animation="wave"
                                key={`header-${idx}`}
                                variant="rectangular"
                                width="100%"
                                height={20}
                                
                            />
                            ))}
                            {[110, 20, 60, 75, 110, 70].map((width, idx) => (
                            <Skeleton animation="wave"
                                key={`value-${idx}`}
                                variant="rectangular"
                                width={width}
                                height={20}
                                
                                style={{ justifySelf: idx === 5 ? 'end' : 'start' }}
                            />
                            ))}
                        </div>
                    </div>
                    <div className="header">
                        <Skeleton animation="wave" variant="rectangular" width="40%" height={36} />
                    </div>
                    <div className="hedge-skeleton-container">
                        <div className="skeleton-header-row">
                            {[20, 100, 100, 100, 100, 80, 90, 80, 90, 100].map((width, i) => (
                            <Skeleton animation="wave" key={`head-${i}`} variant="rectangular" width={width} height={20} />
                            ))}
                        </div>
                        <div className="skeleton-divider" />
                            <div className="skeleton-tsla-row">
                                {[20, 100, 100].map((width, i) => (
                                <Skeleton animation="wave" key={`tsla-${i}`} variant="rectangular" width={width} height={20} />
                                ))}
                            </div>
                            <div className="skeleton-option-row">
                                {[20, 120, 60, 100, 90, 80, 80, 70, 100, 90].map((width, i) => (
                                <Skeleton animation="wave" key={`opt-${i}`} variant="rectangular" width={width} height={20} />
                                ))}
                            </div>
                            <div className="skeleton-button-container">
                                <Skeleton animation="wave" variant="rectangular" width={120} height={36} />
                            </div>
                    </div>
                </div>
            );
        case 'recommendationdashboard':
            const skeletonWidth = [
                { name: 'cap-level-toggle', width: 240 },
                { name: 'global-cap-level', width: 120 },
                { name: 'global-cap-level', width: 180 },
                { name: 'exp-date', width: 150 },
                { name: 'exp-date', width: 180 },
                { name: 'optimize-btn', width: 180 },
                { name: 'list-symbols', width: 200 },
                { name: 'portfolio-holdings', width: 160 },
                { name: 'available-to-write-calls', width: 160 },
                { name: 'portfolio-value', width: 220 },
                { name: 'premium-total', width: 220 },
                { name: 'optimized-premium', width: 220 }
            ];

            const generateSkeletons = (widths) => {
                return widths.map((item, index) => (
                    <div key={index} className={`${item.name}-skeleton`}>
                        <Skeleton animation="wave" variant="rectangular" width={item.width} height={36}  />
                    </div>
                ));
            };

            return (
                <div className="recommendation-dashboard-skeleton">
                    <div className="settings-row-skeleton">
                        {generateSkeletons(skeletonWidth.slice(0, 6))}
                    </div>

                    <div className="summary-row-skeleton">
                        {generateSkeletons(skeletonWidth.slice(6))}
                    </div>

                    <div className="table-header-skeleton">
                        {[...Array(10)].map((_, idx) => (
                            <div key={`header-${idx}`} className="header-cell">
                                <Skeleton animation="wave" variant="text" width={100} height={20}  />
                            </div>
                        ))}
                    </div>

                    {[...Array(rows)].map((_, idx) => (
                        <div key={idx} className="table-row-skeleton">
                            {[...Array(10)].map((_, i) => (
                                <div key={`cell-${i}`} className="cell">
                                    <Skeleton animation="wave" variant={i === 9 ? "rectangular" : "text"} width={i === 9 ? 120 : 100} height={28}  />
                                </div>
                            ))}
                        </div>
                    ))}

                    <div className="preview-trades-skeleton">
                        <Skeleton animation="wave" variant="rectangular" width={180} height={40}  />
                    </div>
                </div>
            );
        case 'profile':
            const featureWidths = [160, 120, 140, 100, 130, 110, 150, 120];
            return (
                    <div className="profile-skeleton">
                         <div className="profile-header-skeleton">
                            <div className="profile-left">
                                <Skeleton animation="wave" variant="circular" width={80} height={80} />
                                <div className="profile-details">
                                <Skeleton animation="wave" variant="text" width={280} height={36} />
                                <Skeleton animation="wave" variant="text" width={250} height={26} />
                                <Skeleton animation="wave" variant="text" width={200} height={22} />
                                </div>
                            </div>

                            <div className="profile-right">
                                <Skeleton animation="wave" variant="rounded" width={130} height={36} />
                                <Skeleton animation="wave" variant="rounded" width={110} height={36} />
                            </div>
                        </div>

                        <div className="active-plan-skeleton">
                            {/* Header */}
                            <div className="plan-header">
                                <Skeleton animation="wave" variant="text" width={300} height={40} />
                            </div>

                            <hr className="divider" />

                            {/* Details Grid */}
                            <div className="plan-details">
                                <div className="plan-item">
                                <span className="dot" />
                                <Skeleton animation="wave" variant="text" width={220} height={26} />
                                </div>
                                <div className="plan-item">
                                <span className="dot" />
                                <Skeleton animation="wave" variant="text" width={240} height={26} />
                                </div>
                                <div className="plan-item">
                                <span className="dot" />
                                <Skeleton animation="wave" variant="text" width={200} height={26} />
                                </div>
                                <div className="plan-item">
                                <span className="dot" />
                                <Skeleton animation="wave" variant="text" width={180} height={26} />
                                </div>
                            </div>
                        </div>

                        <div className="skeleton-loader-wrapper">
                            <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                                {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                            </p>
                        </div>
                        <div className="skeleton-loader-wrapper">
                            <Loader size="small" />
                        </div>

                        <div className="pricing-skeleton">
                            <div className="billing-toggle">
                                <Skeleton animation="wave" variant="rectangular" width={320} height={40} />
                            </div>
                                <div className="plan-cards">
                                    {[1, 2].map((_, index) => (
                                    <div className="plan-card" key={index}>
                                        <Skeleton animation="wave" variant="circular" width={60} height={60} />
                                        <Skeleton animation="wave" variant="text" width={220} height={26} />
                                        <Skeleton animation="wave" variant="text" width={180} height={24} />

                                        <Skeleton animation="wave"
                                        variant="rectangular"
                                        width="100%"
                                        height={40}
                                        style={{ margin: "12px 0" }}
                                        />

                                        <div className="plan-features-column">
                                            {[...Array(8)].map((_, idx) => (
                                                <div className="plan-feature" key={`feature-${idx}`}>
                                                    <Skeleton animation="wave" variant="circular" width={20} height={20} />
                                                    <Skeleton animation="wave"
                                                        variant="rectangular"
                                                        className="skeleton-pill"
                                                        width={featureWidths[idx % featureWidths.length]}
                                                        height={20}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    ))}
                                </div>
                            </div>
                    </div>
            );
        
        case 'multiuser':
            return(
                <div className="user-skeleton">
                    <div className="add-user-card">
                        <Skeleton variant="text" width={140} height={24} animation="wave" />
                        <Skeleton variant="rectangular" width="100%" height={50} animation="wave" />
                        <Skeleton variant="text" width={220} height={18} animation="wave" />
                        <div className="add-btn">
                        <Skeleton variant="rectangular" width={100} height={36} animation="wave" />
                        </div>
                    </div>
                    <div className="skeleton-loader-wrapper">
                            <p className={`skeleton-loading-message ${showAlmostThere ? 'visible' : 'hidden'}`}>
                                {showAlmostThere ? currentMessages[currentMessageIndex] : '\u00A0'}
                            </p>
                        </div>
                    <div className="skeleton-loader-wrapper">
                        <Loader size="small" />
                    </div>
                    <div className="user-table">
                        <div className="table-header">
                            <Skeleton variant="text" width={30} height={20} animation="wave" />
                            <Skeleton variant="text" width={160} height={20} animation="wave" />
                            <Skeleton variant="text" width={120} height={20} animation="wave" />
                            <Skeleton variant="text" width={160} height={20} animation="wave" />
                            <Skeleton variant="text" width={100} height={20} animation="wave" />
                        </div>

                        {[...Array(6)].map((_, idx) => (
                            <div className="table-row" key={idx}>
                                <Skeleton variant="text" width={20} height={20} animation="wave" />
                                <Skeleton variant="text" width={180} height={20} animation="wave" />
                                <Skeleton variant="rectangular" width={100} height={24} animation="wave" />
                                <Skeleton variant="rectangular" width={80} height={24} animation="wave" />
                                <div className="row-actions">
                                <Skeleton variant="circular" width={32} height={32} animation="wave" />
                                <Skeleton variant="rectangular" width={100} height={32} animation="wave" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )

        default:
            return null;
    }
};

export default SkeletonComponent;
